/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import { v4 as uuidv4 } from "uuid";
import { translate } from "../../../../../../../common/providers";
import ViewCreationHeader from "./components/header/ViewCreationHeader";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import styles from "./ViewCreation.module.css";
import "./react-grid-layout-override.css";
import { isNonEmptyArray } from "../../../../../../../common/utils";
import ViewCreationToolbox from "./components/toolbox/ViewCreationToolbox";
import { SECTION } from "../../../constants";
import { CustomButton, CustomDialog, CustomIconButton, icon } from "../../../../../../../common/components";
import SectionNameDialog from "./components/section-name-dialog/SectionNameDialog";
import { useApi, usePrevious } from "../../../../../../../common/hooks";
import { FormStudioService } from "../../../../../../../api";
import { isValidAndNonOverlapping } from "../../../../../../../common/components/customizable-form/section/utils";

const ResponsiveGridLayout = WidthProvider(Responsive);

const MAX_WIDTH = 4;
const UUID_LENGTH = 36;
const initLayout = (id) => [
	{
		h: 1,
		w: 4,
		x: 0,
		y: 0,
		id: id.toString(),
		i: id.toString(),
		uuid: id.toString(),
		name: "My first section",
		isResizable: false,
		type: SECTION.SECTION,
	},
];

const isCriteria = (type) => type !== SECTION.SECTION && type !== SECTION.SUBSECTION;

function generateLayout(sections) {
	if (!isNonEmptyArray(sections)) {
		return [];
	}
	let tempY = 0;
	const newLayout = [];
	const handleSection = (section, type) => {
		const sectionId = section?.uuid || section?.id || Date.now();
		newLayout.push({
			uuid: sectionId.toString(),
			name: section.name,
			i: sectionId.toString(),
			id: sectionId.toString(),
			type,
			isResizable: false,
			x: 0,
			y: tempY,
			w: 4,
			h: 1,
		});
		tempY++;
		if (isNonEmptyArray(section?.layout)) {
			const isValidLayout = isValidAndNonOverlapping(
				section.layout.filter((block) => !block?.hidden),
				MAX_WIDTH
			);
			section.layout.forEach((block) => {
				const { x, y, w, h } = block || {};
				const coordinates = (block?.hidden && {}) ||
					(isValidLayout && { x, y: y + tempY, w, h }) || { x: 0, y: tempY, w: 4, h: 1 };
				newLayout.push({ ...block, i: block.id, ...coordinates });
				if (!isValidLayout) {
					tempY++;
				}
			});
			if (isValidLayout) {
				const { y, h } = newLayout.reduce((max, obj) => (obj.y + obj.h > max.y + max.h ? obj : max)) || {};
				tempY = y + h + 1;
			}
		}
	};
	sections.forEach((section) => {
		handleSection(section, SECTION.SECTION);
		if (isNonEmptyArray(section?.subsection)) {
			section.subsection.forEach((subsection) => handleSection(subsection, SECTION.SUBSECTION));
		}
	});
	return newLayout;
}
function convertDatabaseLayout(layout) {
	const dbSections = [];
	let currentContainer = SECTION.SECTION;
	let isReset = false;
	let currentY = 0;
	const orderedLayout = layout.sort((a, b) => {
		if (a.y !== b.y) {
			return a.y - b.y;
		}
		return a.x - b.x;
	});
	orderedLayout.forEach((block) => {
		if (block?.type === SECTION.SECTION) {
			currentContainer = SECTION.SECTION;
			dbSections.push(
				(block?.uuid &&
					block.uuid.length === UUID_LENGTH && {
						uuid: block.uuid,
						name: block.name,
						layout: [],
						subsection: [],
					}) || {
					name: block.name,
					layout: [],
					subsection: [],
				}
			);
			isReset = true;
		}
		if (![SECTION.SECTION, SECTION.SUBSECTION].includes(block?.type)) {
			const { id, name, options, type, x, y, w, h, uuid } = block;
			if (isReset) {
				currentY = y;
			}
			const blockId = (uuid?.length === UUID_LENGTH && uuid) || (id?.length === UUID_LENGTH && id);
			const dbBlock = { uuid: blockId, id: blockId, name, options, type, x, y: y - currentY, w, h };
			if (currentContainer === SECTION.SECTION) {
				dbSections.at(-1).layout.push(dbBlock);
			} else {
				dbSections.at(-1).subsection.at(-1).layout.push(dbBlock);
			}
			isReset = false;
		}
		if (block?.type === SECTION.SUBSECTION) {
			currentContainer = SECTION.SUBSECTION;
			dbSections.at(-1).subsection.push(
				(block?.uuid &&
					block.uuid.length === UUID_LENGTH && { uuid: block.uuid, name: block.name, layout: [] }) || {
					name: block.name,
					layout: [],
				}
			);
			isReset = true;
		}
	});
	dbSections.forEach((section) => {
		if (!isNonEmptyArray(section.subsection)) {
			delete section.subsection;
		}
		if (!isNonEmptyArray(section.layout)) {
			delete section.layout;
		}
	});
	return { sections: dbSections };
}

const brandNewName = "dsi.form-studio.view.creation.name";

export default function ViewCreation({ view = null, onGoBack }) {
	const [name, setName] = useState(translate(brandNewName));
	const [layouts, setLayouts] = useState([]);
	const [openWarning, setOpenWarning] = useState(false);
	const [temporaryBlock, setTemporaryBlock] = useState(null);
	const [openNameDialog, setOpenNameDialog] = useState(false);
	const [temporaryType, setTemporaryType] = useState(null);
	const { call: createView } = useApi(FormStudioService.createView);
	const { call: updateView } = useApi(FormStudioService.updateView);
	const { call: getView } = useApi(FormStudioService.getView);
	const previousLayouts = usePrevious(layouts);
	const isValidView = useMemo(
		() =>
			(!view && name?.length > 0) ||
			(view && ((name?.length > 0 && name !== view?.name) || view.layout !== JSON.stringify(layouts))),
		[layouts, name, view]
	);
	const fieldIds = useMemo(
		() =>
			layouts
				.filter((element) => element.type !== SECTION.SECTION && element.type !== SECTION.SUBSECTION)
				.map(
					(element) =>
						(element?.uuid?.length === UUID_LENGTH && element.uuid) ||
						(element?.id?.length === UUID_LENGTH && element.id)
				),
		[layouts]
	);
	useEffect(() => {
		if (
			isNonEmptyArray(previousLayouts) &&
			isNonEmptyArray(layouts) &&
			(layouts || []).find((item) => item.y === 0)?.type !== SECTION.SECTION
		) {
			setLayouts(previousLayouts);
		}
	}, [layouts, previousLayouts]);
	useEffect(() => {
		if (!view?.id) {
			setLayouts(initLayout(uuidv4()));
			setName(translate(brandNewName));
		} else {
			getView({ pageId: view.id })
				.then((data) => {
					setName(data.name);
					const savedLayout = JSON.parse(data?.layout);
					setLayouts(generateLayout(savedLayout?.sections));
				})
				.catch(console.error);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [view]);
	const handleChangeName = (value) => {
		setName(value);
	};
	const handleLayoutChange = (_, allLayouts) => {
		setLayouts((prevLayouts) =>
			allLayouts.lg.map((newItem) => {
				const existing = prevLayouts.find((item) => item.i === newItem.i);
				return {
					...existing,
					...newItem,
				};
			})
		);
	};
	const handleMove = (newLayout) => {
		setLayouts((prev) =>
			newLayout.map((block) => {
				const currentBlock = prev.find((prevBlock) => prevBlock.i === block.i);
				return { ...currentBlock, ...block };
			})
		);
	};
	const handleDrop = (layout, layoutItem) => {
		if (!layoutItem) {
			return;
		}
		const { uuid, id, name: tempName, type } = temporaryType || {};
		const newItem = {
			...layoutItem,
			i: id.toString(),
			id: id.toString(),
			name: tempName,
			w: 4,
			type,
			isResizable: isCriteria(type),
		};
		newItem.uuid = uuid || uuidv4();

		if (!layouts.some((block) => block.id === newItem.id)) {
			setLayouts((prev) =>
				layout.map(
					(block) =>
						(block.i === layoutItem.i && newItem) || {
							...prev.find((prevBlock) => prevBlock.i === block.i),
							...block,
						}
				)
			);
		}
		setTemporaryType(null);
	};
	const handleResize = (layout, layoutItem, resizedItem) => {
		setLayouts((prev) => prev.map((item) => (item.i === resizedItem.i && { ...item, ...resizedItem }) || item));
	};
	const handleAddToLayout = (temporaryItem) => {
		setTemporaryType(temporaryItem);
	};
	const stopPropagation = (e) => {
		e.stopPropagation();
	};
	const handleDelete = (block) => {
		setLayouts((prev) => prev.filter((layedBlock) => layedBlock.id !== block.id));
	};
	const handleOpenNameDialog = () => {
		setOpenNameDialog((prev) => !prev);
	};
	const handleEdit = (block) => {
		setTemporaryBlock(block);
		handleOpenNameDialog();
	};
	const handleRenameSection = (newName) => {
		setLayouts((prev) =>
			prev.map(
				(prevBlock) => (prevBlock.id === temporaryBlock?.id && { ...prevBlock, name: newName }) || prevBlock
			)
		);
		setTemporaryBlock(null);
	};
	const handleSubmit = () => {
		if (isValidView) {
			const payload = {
				name,
				layout: JSON.stringify(convertDatabaseLayout(layouts)),
				fieldIds,
				description: view?.description || "",
			};
			if (view?.id) {
				updateView({ pageId: view.id }, payload).then(onGoBack).then(console.error);
			} else {
				createView(payload).then(onGoBack).then(console.error);
			}
		}
	};
	const handleOpenWarning = () => {
		setOpenWarning((prev) => !prev);
	};
	return (
		<>
			<ViewCreationHeader name={name} onChangeName={handleChangeName} onGoBack={handleOpenWarning} />
			<div className={styles.container}>
				<ViewCreationToolbox usedFields={fieldIds} onAdd={handleAddToLayout} />
				<div className={styles.formStudio}>
					<div>
						<ResponsiveGridLayout
							isDraggable
							isDroppable
							isResizable
							useCSSTransforms
							breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
							className="layout"
							cols={{ lg: 4, md: 4, sm: 3, xs: 2, xxs: 1 }}
							compactType="vertical"
							layouts={{ lg: layouts.filter((block) => !block?.hidden) }}
							margin={[8, 8]}
							measureBeforeMount={false}
							rowHeight={48}
							onDragStop={handleMove}
							onDrop={handleDrop}
							onLayoutChange={handleLayoutChange}
							onResizeStop={handleResize}
						>
							{isNonEmptyArray(layouts) &&
								layouts
									.filter((block) => !block?.hidden)
									.map((block) => (
										<div
											key={block.id}
											className={styles.block}
											data-is-criteria={isCriteria(block.type)}
											data-is-section={block?.type === SECTION.SECTION}
										>
											<span>{block.name}</span>
											{!isCriteria(block.type) && (
												<CustomIconButton
													icon={icon.faPen}
													size="sm"
													onClick={() => handleEdit(block)}
													onMouseDown={stopPropagation}
													onTouchStart={stopPropagation}
												/>
											)}
											<CustomIconButton
												icon={icon.faTrash}
												size="sm"
												onClick={() => handleDelete(block)}
												onMouseDown={stopPropagation}
												onTouchStart={stopPropagation}
											/>
										</div>
									))}
						</ResponsiveGridLayout>
					</div>
				</div>
			</div>
			<div className={styles.footer}>
				<CustomButton color="secondary" variant="outlined" onClick={handleOpenWarning}>
					{translate("common:btn.back")}
				</CustomButton>
				<CustomButton disabled={!isValidView} variant="contained" onClick={handleSubmit}>
					{translate("common:btn.validate")}
				</CustomButton>
			</div>
			<SectionNameDialog
				name={temporaryBlock?.name}
				open={openNameDialog}
				onClose={handleOpenNameDialog}
				onRename={handleRenameSection}
			/>
			<CustomDialog
				iconColor="var(--red-500)"
				iconModel={icon.faExclamationTriangle}
				open={openWarning}
				submitLabel={translate("common:btn.confirm")}
				subTitle={translate("dsi.form-studio.view.creation.cancel.warning")}
				onClose={handleOpenWarning}
				onSubmit={onGoBack}
			/>
		</>
	);
}
